import React from 'react';
import styled, { css } from 'styled-components';

import { TagColors } from '../../theme/colors';
import { colorScheme } from '../../theme';
import { Tooltip } from './tooltip';

interface StyledButtonProps {
  tooltip?: string;
  variant?:
    | 'primary'
    | 'secondary'
    | 'warning'
    | 'ternary'
    | 'accept'
    | 'danger'
    | 'gray'
    | 'tag'
    | 'link'
    | 'likeText'
    | 'login'
    | 'tagWithIconPrimary'
    | 'tagWithIconSecondary';
  block?: boolean;
  fullWidth?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  readonly?: boolean;
  $isAnchor?: boolean;
}

export interface ButtonProps extends StyledButtonProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  as?: 'button' | 'a' | 'h1';
  href?: string;
  target?: string;
  rel?: string;
}

const basicStyle = {
  borderRadius: '8px',
  borderWidth: '1px',
  background: 'transparent',
  borderColor: 'transparent',
  borderStyle: 'solid',
  height: '40px',
  cursor: 'pointer',
  margin: 0,
  whiteSpace: 'nowrap',
  fontWeight: '400',
  display: 'inline-block',
  color: colorScheme.text.base,
};

const variantStyles = (variant = 'primary') =>
  ({
    primary: css`
      ${basicStyle}
      color: white;
      border-color: ${colorScheme.buttons.primary.border};
      background: ${colorScheme.buttons.primary.background};

      &:focus {
        background: ${colorScheme.buttons.primary.hover};
        border-color: ${colorScheme.buttons.primary.hover};
      }
    `,
    secondary: css`
      ${basicStyle}
      color:  ${colorScheme.buttons.secondary.text};
      border-color: ${colorScheme.buttons.secondary.border};
      background: ${colorScheme.buttons.secondary.background};

      &:focus {
        border-color: ${colorScheme.buttons.secondary.hover};
        background: ${colorScheme.buttons.secondary.hover};
      }
    `,
    ternary: css`
      ${basicStyle}
      color: ${colorScheme.buttons.ternary.text};
      border-color: ${colorScheme.buttons.ternary.border};
      background-color: ${colorScheme.buttons.ternary.background};

      &:hover {
        color: ${colorScheme.buttons.ternary.hover};
        background: ${colorScheme.buttons.ternary.background};
      }
    `,
    accept: css`
      ${basicStyle}
      color: ${colorScheme.buttons.accept.text};
      background-color: ${colorScheme.buttons.accept.background};
      border-color: ${colorScheme.buttons.accept.border};
      &:hover,
      &:focus {
        background: color-mix(in srgb, ${colorScheme.buttons.accept.background} 90%, white);
        border-color: color-mix(in srgb, ${colorScheme.buttons.accept.border} 90%, white);
      }
    `,
    danger: css`
      ${basicStyle}
      color: ${colorScheme.buttons.danger.text};
      background-color: ${colorScheme.buttons.danger.background};
      border-color: ${colorScheme.buttons.danger.border};
      &:hover,
      &:focus {
        background: color-mix(in srgb, ${colorScheme.buttons.danger.background} 90%, white);
        border-color: color-mix(in srgb, ${colorScheme.buttons.danger.border} 90%, white);
      }
    `,
    warning: css`
      ${basicStyle}
      color: ${colorScheme.buttons.warning.text};
      background-color: ${colorScheme.buttons.warning.background};
      border-color: ${colorScheme.buttons.warning.border};
      &:hover,
      &:focus {
        background: color-mix(in srgb, ${colorScheme.buttons.warning.background} 90%, white);
        border-color: color-mix(in srgb, ${colorScheme.buttons.warning.border} 90%, white);
      }
    `,
    gray: css`
      ${basicStyle}
      color: ${colorScheme.buttons.gray.text};
      background-color: ${colorScheme.buttons.gray.background};
      border-color: ${colorScheme.buttons.gray.background};
    `,
    tag: css`
      ${basicStyle}
      color: ${colorScheme.buttons.tag.text};
      background-color: ${colorScheme.buttons.tag.background};
      border-color: ${colorScheme.buttons.tag.border};
    `,
    link: css`
      ${basicStyle}
      border: none;
      color: ${colorScheme.text.link};
      font-size: 16px;
      line-height: 24px;
      background: transparent;
      cursor: pointer;
      transition: 150ms linear all;
      &:disabled {
        opacity: 0.3;
      }
    `,
    likeText: css`
      ${basicStyle}
      border: none;
      transition: 150ms linear all;
      &:disabled {
        opacity: 0.3;
      }
    `,
    login: css`
      ${basicStyle}
      color: white;
      border-color: ${colorScheme.buttons.primary.border};
      background: ${colorScheme.buttons.primary.background};
      display: flex;
      flex-direction: row;
      justify-content: center;
      &:hover,
      &:focus {
        background: ${colorScheme.buttons.primary.hover};
        border-color: ${colorScheme.buttons.primary.hover};
      }

      img {
        margin-right: 0.5em;
      }
    `,
    tagWithIconPrimary: css`
      ${basicStyle}
      background: ${colorScheme.brand};
      color: white;
      padding-top: 7px;
      padding-bottom: 7px;
      padding-right: 36px;
      padding-left: 14px;
    `,
    tagWithIconSecondary: css`
      ${basicStyle}
      background: ${TagColors.backgroundLight};
      color: ${colorScheme.brand};
      padding-top: 7px;
      padding-bottom: 7px;
      padding-right: 36px;
      padding-left: 14px;
    `,
  })[variant];

const sizeStyles = (size = 'md') =>
  ({
    sm: css`
      padding: 4px 8px;
      height: auto;
      font-size: 0.75rem;
    `,
    md: css`
      font-size: 16px;
      line-height: 24px;
      padding: 7px 16px;
    `,
    lg: css`
      padding: 8px 24px;
      height: auto;
      font-size: 1.25rem;
    `,
    xs: css`
      padding: 0;
      height: auto;
      font-size: 16px;
      line-height: 24px;
    `,
  })[size];

const StyledButton = styled.button.attrs({ tabIndex: 0 })<StyledButtonProps>`
  & > input[type='file'] {
    display: none;
  }
  transition: 150ms linear all;
  text-decoration: none;
  display: inline-block;

  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
  }

  ${({ block }) =>
    block &&
    css`
      display: block;
    `};

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `};

  // When rendered as an anchor (as="a"), wrap variant styles in && to increase CSS specificity.
  // This ensures variant colors override global anchor styles (e.g., a { color: blue }).
  // For regular buttons, normal specificity is used so container styles can override display.
  ${({ variant, $isAnchor }) =>
    $isAnchor
      ? css`
          && {
            ${variantStyles(variant)}
          }
        `
      : variantStyles(variant)}
  ${({ size }) => sizeStyles(size)}
  ${({ readonly }) =>
    readonly &&
    css`
      pointer-events: none;
      cursor: default;
    `}

  .spinner-border.spinner-border-sm {
    position: relative;
    top: 3px;
  }

  &:disabled,
  &[disabled] {
    cursor: default;
    opacity: 0.6;
  }
`;

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
  const { as: Component = 'button', tooltip, type, href, target, rel, tabIndex, ...rest } = props;
  const buttonType = Component === 'button' ? type || 'button' : undefined;
  const anchorProps = Component === 'a' ? { href, target, rel } : {};
  const isAnchor = Component === 'a';

  if (tooltip) {
    return (
      <Tooltip tooltip={tooltip}>
        <StyledButton
          as={Component}
          ref={ref as React.RefObject<HTMLButtonElement>}
          type={buttonType}
          tabIndex={tabIndex as 0 | undefined}
          $isAnchor={isAnchor}
          {...anchorProps}
          {...rest}
        >
          {props.children}
        </StyledButton>
      </Tooltip>
    );
  }

  return (
    <StyledButton
      as={Component}
      ref={ref as React.RefObject<HTMLButtonElement>}
      type={buttonType}
      tabIndex={tabIndex as 0 | undefined}
      $isAnchor={isAnchor}
      {...anchorProps}
      {...rest}
    >
      {props.children}
    </StyledButton>
  );
});
